import { ApiError, NetworkError } from '@fewerbags/api-client';
import { ZodError } from 'zod';

/** One place that turns API error codes into words a person can act on. */
const MESSAGES: Record<string, string> = {
  AUTH_REQUIRED: 'Please sign in to continue.',
  INVALID_CREDENTIALS: 'That email and password don’t match.',
  EMAIL_TAKEN: 'An account with that email already exists. Try signing in instead.',
  EMAIL_NOT_VERIFIED: 'Confirm your email address first — open the link we sent you.',
  CAPTCHA_REQUIRED: 'Please complete the “are you a person” check first.',
  CAPTCHA_FAILED: 'The check didn’t pass. Try the captcha again.',
  CAPTCHA_UNAVAILABLE: 'The captcha service couldn’t be reached. Wait a moment and try again.',
  HOUSEHOLD_REQUIRED: 'Create or join a household first.',
  HOUSEHOLD_DISABLED: 'This household has been disabled. Contact support@five01creations.com.',
  ROLE_REQUIRED: 'You don’t have permission to do that in this household.',
  ADMIN_REQUIRED: 'Administrators only.',
  CSRF_ORIGIN: 'The request was blocked for safety. Reload the page and try again.',
  CSRF_FETCH_SITE: 'The request was blocked for safety. Reload the page and try again.',
  INVALID_TOKEN: 'That link is invalid, expired, or has already been used. Request a new one.',
  INVITE_INVALID: 'That invite code isn’t valid, has expired, or was already used.',
  INVITE_NOT_FOUND: 'That invite no longer exists.',
  NOT_A_MEMBER: 'You are not a member of that household.',
  MEMBER_NOT_FOUND: 'That person is no longer a member of this household.',
  PERSON_NOT_FOUND: 'That person is no longer in this household. Reload to see the latest list.',
  PERSON_HAS_ACCOUNT:
    'This person has their own account: they change their name in their profile, and leave from the members list.',
  HOUSEHOLD_NOT_FOUND: 'That household doesn’t exist.',
  HOUSEHOLD_HAS_MEMBERS: 'Remove the other members before deleting the household.',
  LAST_OWNER: 'A household needs at least one owner. Make someone else an owner first.',
  OWNERSHIP_TRANSFER_REQUIRED:
    'You are the only owner of a household with other members. Choose who takes over first.',
  INVALID_TRANSFER_TARGET: 'Choose a current member of the household to take over.',
  SESSION_NOT_FOUND: 'That device session no longer exists.',
  TOKEN_INVALID: 'That device session is no longer valid. Sign in again on the device.',
  DEVICE_NOT_FOUND: 'That device is no longer signed in.',
  RESEND_THROTTLED:
    'A verification email was sent recently. Check your inbox or try again in a few minutes.',
  PASSKEY_NOT_FOUND: 'That passkey no longer exists.',
  PASSKEY_VERIFY_FAILED: 'Passkey sign-in didn’t complete. Try again or use your password.',
  INVALID_PASSKEY_RESPONSE: 'Passkey sign-in didn’t complete. Try again or use your password.',
  PASSKEY_CHALLENGE_EXPIRED: 'That passkey prompt expired. Try again.',
  RECIPE_NOT_FOUND: 'That recipe doesn’t exist or isn’t shared with you.',
  RECIPE_NOTE_NOT_FOUND: 'That note was already deleted. Reload to see the latest notes.',
  NOTE_ORDER_MISMATCH: 'The notes changed while you were reordering. Reload and try again.',
  INGREDIENT_NOT_FOUND: 'That ingredient doesn’t exist.',
  PANTRY_ITEM_NOT_FOUND: 'That pantry item no longer exists.',
  STORE_NOT_FOUND: 'That store doesn’t exist.',
  PLANNER_ALREADY_PLANNED:
    'One of those days was planned in the meantime. Refresh the proposal and try again.',
  PLANNER_DUPLICATE_DAY: 'Each day can only be added once.',
  PLANNER_LEFTOVERS_SOURCE: 'Packed leftovers need the dinner they come from on the plan.',
  PLACE_NOT_FOUND: 'That place no longer exists. Reload to see the latest list.',
  PLACE_HOME_EXISTS: 'Your household already has its home place.',
  PLACE_HOME_KIND: 'Home stays home: add another place for work or school.',
  PLACE_IS_HOME: 'The home place can’t be removed.',
  DINE_OUT_HAS_NO_RECIPES: 'A dining-out meal has no recipes. Change it to a cooked meal first.',
  PLAN_HAS_RECIPES: 'Remove the recipes before marking this meal as dining out.',
  LEFTOVERS_KIND: 'A leftovers meal stays leftovers.',
  PRODUCT_NOT_FOUND: 'That product doesn’t exist.',
  PRODUCT_INGREDIENT_MISMATCH: 'That product is for a different ingredient.',
  GROCERY_LIST_NOT_FOUND: 'That grocery list no longer exists.',
  GROCERY_ITEM_NOT_FOUND: 'That item is no longer on this list.',
  ALREADY_CHECKED: 'Someone already checked that item off. Refresh to see the list.',
  CHECK_CHANGED: 'Someone checked that item off again, so it stayed checked.',
  MEDIA_NOT_FOUND: 'That photo doesn’t exist.',
  MEDIA_IN_USE: 'That photo is still used by a recipe or ingredient.',
  NOT_AN_IMAGE: 'Please choose an image file.',
  UNSUPPORTED_IMAGE:
    'That image format isn’t supported yet. On iPhone, choose “Most Compatible” in Camera settings or share as JPEG.',
  UPLOAD_TOO_LARGE: 'That image is too large (15 MB max).',
  IMAGE_TOO_SMALL: 'That image is too small to use.',
  IMPORT_PHOTO_DISABLED: 'Photo import isn’t turned on for this server.',
  IMPORT_PHOTO_UNREADABLE:
    'No recipe could be read from that photo. Try a sharper, well-lit photo of the whole card.',
  IMPORT_PHOTO_FAILED: 'The recipe reader isn’t available right now. Try again in a few minutes.',
  IMPORT_PHOTO_RATE_LIMITED: 'The recipe reader is busy. Wait a minute and try again.',
  MEDIA_QUOTA_EXCEEDED: 'Your household’s photo storage is full. Remove some photos to add more.',
  INGREDIENTS_REQUIRED: 'Add at least one ingredient.',
  MISSING_DENSITY:
    'Weights and volumes can’t be swapped for this ingredient yet. Add its density on the ingredient page.',
  MISSING_UNIT_WEIGHT:
    'Counts and weights can’t be swapped for this ingredient yet. Add its average weight per item on the ingredient page.',
  INCOMPATIBLE_UNITS: 'Those units can’t be converted into each other.',
  ALIAS_TAKEN: 'That name is already used by another ingredient.',
  RATE_LIMITED: 'Too many attempts. Please wait a minute and try again.',
  CALENDAR_INVALID:
    'That address didn’t return a calendar. Copy the “Secret address in iCal format” again.',
  CALENDAR_FETCH_FAILED: 'Couldn’t download that calendar. Check the address and try again.',
  CALENDAR_FEED_NOT_FOUND: 'That calendar is no longer connected.',
  TOO_MANY_CALENDARS: 'You can connect up to 10 calendars.',
  BUSY_BLOCK_NOT_FOUND: 'That busy time no longer exists.',
  INVALID_TIME_RANGE: 'The end time has to be different from the start time.',
  REMINDER_NOT_FOUND: 'That reminder no longer exists.',
  PLAN_STATUS_VIA_COOKED: 'Use Mark cooked so the pantry is updated too.',
  LEFTOVERS_OF_LEFTOVERS: 'This meal is already leftovers. Save leftovers from the original meal.',
  LEFTOVERS_BEFORE_SOURCE: 'Leftovers can’t be planned before the meal they come from.',
  LEFTOVERS_HAVE_NO_RECIPES: 'A leftovers meal uses the recipes of the meal it comes from.',
  ALREADY_REPORTED: 'Your household already reported this. We will take a look.',
  REPORT_NOT_FOUND: 'That report no longer exists.',
  PHOTO_NOT_FOUND: 'That photo no longer exists.',
  RECEIPT_SCAN_UNAVAILABLE:
    'Receipt scanning isn’t turned on for this server. You can still check items off one by one.',
  RECEIPT_UNREADABLE:
    'No receipt could be read from that photo. Flatten the receipt, fill the frame and try again.',
  RECEIPT_SCAN_FAILED: 'The receipt reader isn’t available right now. Try again in a few minutes.',
  RECEIPT_SCAN_RATE_LIMITED: 'The receipt reader is busy. Wait a minute and try again.',
  RECEIPT_DUPLICATE_ITEM: 'Two receipt lines are matched to the same item. Pick one.',
  GROCERY_LIST_NOT_OPEN: 'This list is done or archived. Reopen it to add a receipt.',
  INGREDIENT_NAME_REQUIRED: 'Give each new item a name.',
};

export function messageForError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code && MESSAGES[err.code]) return MESSAGES[err.code]!;
    if (err.status === 429) return MESSAGES['RATE_LIMITED']!;
    if (err.status === 401) return MESSAGES['AUTH_REQUIRED']!;
    if (err.status >= 500) return 'The server had a problem. Please try again in a moment.';
    return err.message || 'Something went wrong.';
  }
  if (err instanceof NetworkError) return 'Can’t reach the server. Check your connection.';
  if (err instanceof ZodError) return err.issues[0]?.message ?? 'Please check the form.';
  if (err instanceof Error && err.name === 'NotAllowedError') {
    return 'The passkey prompt was cancelled.';
  }
  if (err instanceof Error) return err.message || 'Something went wrong.';
  return 'Something went wrong.';
}

export function isApiCode(err: unknown, code: string): boolean {
  return err instanceof ApiError && err.code === code;
}
